import { css } from '@linaria/core'
import { type ReactNode, useLayoutEffect } from 'react'

import { viewWidths, type ZoomLevel, zoomLevelValues } from '#store/layoutConfiguration'
import { useSharedAppSelector } from '#store/sharedBaseStore'
import { style } from '#styles'
import { sharedTestIds } from '#utils/sharedTestIds'

const layoutClass = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  margin: auto;
  background: ${style.theme.backgroundSurfacePrimary};
  color: ${style.theme.textDefault};

  box-shadow: 0px 0px 55px -15px rgba(0, 0, 0, 0.25);
  transition: 0.15s max-width ease-out;

  &[data-wideness='${viewWidths.slim}'] {
    max-width: 1024px;
  }

  &[data-wideness='${viewWidths.regular}'] {
    max-width: 1600px;
  }

  &[data-wideness='${viewWidths.wide}'] {
    max-width: 1920px;
  }

  &[data-wideness='${viewWidths.ultraWide}'] {
    max-width: 2560px;
  }

  @page {
    size: A4 portrait;

    padding: 10mm 40mm;
  }

  @media print {
    & {
      max-width: unset !important;
      box-shadow: none;
      zoom: 1 !important;
      margin: unset !important;
      overflow: visible;
    }
  }
  
  &:has(.has-full-width-underlay) {
    background: transparent;
    box-shadow: none;
  }
`

css`
  :global() {
    @media print {
      #root {
        contain: unset !important;
      }
      body {
        overflow: visible;
      }
    }
    #root {
      contain: content;
    }

    ${Object.keys(zoomLevelValues)
      .map(
        (key) => `
    body[data-zoom='${key}'] {
      zoom: ${zoomLevelValues[key as ZoomLevel]};
      --inverse-zoom: calc(1 / ${zoomLevelValues[key as ZoomLevel]});
    }
   `,
      )
      .join('\n\n')}
  }
`

export const AppLayout = ({ children }: { children: ReactNode }) => {
  const {
    zoom: { zoom },
    width: { width },
  } = useSharedAppSelector((x) => x.layoutConfiguration)
  const { theme } = useSharedAppSelector((x) => x.componentConfiguration)

  useLayoutEffect(() => {
    document.body.setAttribute('data-theme', theme)
  }, [theme])

  useLayoutEffect(() => {
    document.body.setAttribute('data-zoom', zoom)
  }, [zoom])

  const isFirefox = navigator.userAgent.toLowerCase().includes('firefox')
  if (isFirefox) {
    document.body.setAttribute('data-browser', 'firefox')
  }

  return (
    <div className={layoutClass} data-wideness={width} data-testid={sharedTestIds.layout.maicApp}>
      {children}
    </div>
  )
}
